import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Content, TableColumnSpec, URL_PATH } from '@mobi/oiv-viewer-utils-ng-jslib';
import { CHAPTER_NAME, HierarchyItem, OIV_TYPE, OivModel } from '@mobi/oiv-viewer-xml-parser-ng-jslib';

import { SUBHEAD_EID, SUBTIT_EID, TIT_EID } from '../oiv-search/index-search/model/common.instant';

import { ContentItem, NavigationItem, ObjectBase } from './model/atomic-model';

@Injectable({
    providedIn: 'root',
})
export class Helper {
    boldRegex = '<bold.*?>(.*?)</bold>';
    boldItalicRegex = '<bold-italic.*?>(.*?)</bold-italic>';
    italicRegex = '<italic.*?>(.*?)</italic>';
    underlineRegex = '<underline.*?>(.*?)</underline>';
    subscriptRegex = '<sub.*?>(.*?)</sub>';
    superscriptRegex = '<sup.*?>(.*?)</sup>';

    regexTagsMap = new Map([
        [this.boldRegex, 'b'],
        [this.boldItalicRegex, 'i,b'],
        [this.italicRegex, 'i'],
        [this.underlineRegex, 'u'],
        [this.subscriptRegex, 'sub'],
        [this.superscriptRegex, 'sup'],
    ]);

    buildChapter(chapter: Content): ObjectBase {
        if (chapter.type === OIV_TYPE.CHAPTER) {
            const topics: ObjectBase[] = [];
            const transformChapter: ObjectBase = {
                title: '',
                eId: chapter.eId,
                sections: [],
            };
            (chapter.content as Content[]).forEach(item => {
                if (item.type === OIV_TYPE.CHAPTER_TITLE) {
                    transformChapter.title = item.content as string;
                }
                // Get topics of the chapter
                if (item.type === OIV_TYPE.TOPIC && item.content instanceof Array && item.content.length > 0) {
                    const subTopics: ObjectBase[] = [];
                    const transformTopic: ObjectBase = {
                        eId: item.eId as string,
                        title: '',
                        orderNumber: '',
                        sections: [],
                    };
                    (item.content as Content[]).forEach(topic => {
                        if (topic.type === OIV_TYPE.TOPIC_TITLE) {
                            transformTopic.title = topic.content as string;
                            transformTopic.orderNumber = topic.attributeOrder as string;
                        }

                        // Get subtopics of the topic
                        const subTopic = {
                            eId: topic.eId as string,
                            title: '',
                            orderNumber: '',
                            sections: [],
                        } as ObjectBase;
                        if (
                            topic.type === OIV_TYPE.CONTAINER &&
                            topic.content instanceof Array &&
                            topic.content.length > 0
                        ) {
                            (topic.content as Content[]).forEach(item => {
                                if (item.type === OIV_TYPE.SECTION_TITLE) {
                                    subTopic.title = item.content as string;
                                    subTopic.orderNumber = item.attributeOrder as string;
                                    subTopics.push(subTopic);
                                }
                            });
                        }
                        transformTopic.sections = subTopics;
                    });
                    topics.push(transformTopic);
                }
            });
            transformChapter.sections = topics;
            return transformChapter;
        }
        return {
            title: '',
            eId: '',
            sections: [],
        };
    }

    buildNavigationItems(objectBases: ObjectBase[], parentIsTopic = false): NavigationItem[] {
        const chapterRoute = URL_PATH.CHAPTERS as string;
        const topicsRoute = URL_PATH.TOPICS as string;
        return objectBases.map(objectBase => {
            let urlLink;

            // Case: parent of a group is topic
            if (parentIsTopic) {
                // Build url link for the topic
                urlLink = `${topicsRoute}/${objectBase.eId}`;
            } else {
                // Case: parent of a group is chapter
                if (objectBase.sections.length === 0) {
                    urlLink = `${chapterRoute}/${objectBase.eId}`;
                } else {
                    urlLink = `${chapterRoute}/${objectBase.eId}/${topicsRoute}`;
                }
            }
            return {
                urlLink,
                title: objectBase.title,
                orderNumber: objectBase.orderNumber,
                subItems: objectBase.sections.map(section => {
                    let urlLink;
                    if (parentIsTopic) {
                        urlLink = `${topicsRoute}/${objectBase.eId}#${section.eId}`;
                    } else {
                        urlLink = `${chapterRoute}/${objectBase.eId}/${topicsRoute}/${section.eId}`;
                    }
                    return {
                        title: section.title,
                        urlLink,
                        orderNumber: section.orderNumber,
                        subItems: [],
                    } as NavigationItem;
                }),
            } as NavigationItem;
        });
    }

    mappingToContents(oivTree: OivModel[]): Content[] {
        return oivTree
            .map(item => {
                return {
                    eId: item.oivAttr_eID,
                    type: item.oivType,
                    hierarchy: item.hierarchy,
                    content: item.content?.length > 0 ? this.#mapOivModelToContent(item.content) : item.text,
                    ...(item['chapter_group_name'] !== undefined && { chapterGroupName: item['chapter_group_name'] }),
                } as Content;
            })
            .map(item => this.#moveColspecForTableToTheParentNode(item));
        // Move the configuration for the table from the config in the first row to the configuration array.
    }

    #mapOivModelToContent(oivModels: OivModel[]): Content[] {
        return oivModels.map(oivModel => {
            return {
                eId: oivModel.oivAttr_eID,
                type: oivModel.oivType,
                hierarchy: oivModel.hierarchy,
                content: oivModel.content?.length > 0 ? this.#mapOivModelToContent(oivModel.content) : oivModel.text,
                ...(oivModel['oivAttr_type'] !== undefined && { attributeType: oivModel['oivAttr_type'] }), //special case for oivModel have the own attributeType
                ...(oivModel['oivAttr_seq'] !== undefined && { attributeSequence: oivModel['oivAttr_seq'] }),
                ...(oivModel['oivAttr_chapter-full'] !== undefined && {
                    attributeOrder: oivModel['oivAttr_chapter-full'],
                }),
                ...(Object.keys(this.#extractTableSpec(oivModel)).length > 0 && {
                    tableSpec: this.#extractTableSpec(oivModel),
                }),
            } as Content;
        });
    }

    getListItems(item: Content): Content[] | string {
        // Loop through the items in the bullet/numbered list
        return Array.isArray(item.content)
            ? this.getContentAsArray(item).map(listItem => {
                  return {
                      eId: listItem.eId,
                      type: listItem.type,
                      content: Array.isArray(listItem.content)
                          ? this.getContentAsArray(listItem).map(contentItem => {
                                return {
                                    eId: contentItem.eId,
                                    type: contentItem.type,
                                    content: Array.isArray(contentItem.content)
                                        ? this.getListItems(contentItem)
                                        : contentItem.content,
                                };
                            })
                          : listItem.content,
                  };
              })
            : item.content;
    }

    getContentAsArray(item: Content): Content[] {
        return item.content as Content[];
    }

    createContent<T extends ContentItem>(item: Content): T {
        return {
            eId: item.eId,
            type: item.type,
            content: item.content,
            attributeOrder: item.attributeOrder,
        } as T;
    }

    createAnchorListContent(item: Content, sectionId: string): ContentItem {
        return {
            eId: sectionId,
            type: item.type,
            content: item.content as string,
        };
    }

    /**
     * Maps a sequence letter to its 1-based counter index for the <ol> `start` attribute.
     * Single letters: a=1 ... z=26. Repeated letters (SGN-3220): aa=27, bb=28 ... zz=52. Empty defaults to 1.
     */
    getAlphabetIndex(letter: string): number {
        if (!letter) {
            return 1;
        }
        const lower = letter.toLowerCase();
        return (lower.length - 1) * 26 + (lower.charCodeAt(0) - 96);
    }

    getContentAsString(content: Content): string {
        return content.content as string;
    }

    contentIsString(item: Content): boolean {
        return typeof item.content === 'string';
    }

    contentIsArray(item: Content): boolean {
        return Array.isArray(item.content);
    }

    parseWithBoldItalicXmlTags(content: string): string {
        this.regexTagsMap.forEach((tags, reg) => {
            content = content.replace(new RegExp(reg, 'g'), (match, text) => {
                tags.split(',').forEach(tagName => {
                    text = `<${tagName}>${text}</${tagName}>`;
                });
                return text;
            });
        });
        return content;
    }

    parseLink(content: string): string {
        // eslint-disable-next-line sonarjs/slow-regex
        const linkRegex = /<link.*?(?:url|target|eID)="(.*?)".*?>(.*?)<\/link>/g;
        return content.replace(linkRegex, (match, attrValue, linkText) => {
            const url = attrValue.startsWith('https://') ? attrValue : 'https://' + attrValue;
            return `<a href="${url}" target="_blank">${linkText}</a>`;
        });
    }

    // This function handles the new line (\n) with removing or replacing it with <br> tag.
    // It skips adding <br> when \n is followed by styling tags without text content.
    handleNewLine(content: string, removeNewLine = false): string {
        if (removeNewLine) {
            return content.replace(/\n/g, '');
        }

        // First, remove newlines that are followed by whitespace and then a tag
        // This handles cases like "\n            <bold>" where we don't want <br>
        let result = content.replace(/\n[ \t]*</g, '<');

        // Then replace remaining newlines with <br>
        result = result.replace(/\n/g, '<br>');

        return result;
    }

    // This function removes the last dot from the input string if it is a parent section.
    removeLastDot(inputString: string, isParent: boolean) {
        if (isParent && inputString.endsWith('.')) {
            return inputString.slice(0, -1);
        }
        return inputString;
    }

    getHomePageURL(router: Router, source: string, isApprovalMode: boolean): string {
        return `${this.getBaseUrl(router, source, isApprovalMode)}/homepage`;
    }

    getBaseUrl(router: Router, source: string, isApprovalMode: boolean): string {
        const urlSegments = router.url.split('/');
        const validLanguageCodes = ['de', 'fr', 'it'];

        if (source === 'B2C') {
            // For B2C, check if there's a language parameter in the URL by examining the second segment
            // Protekta B2C has :language route parameter: /de/avb/urlName
            // Mobiliar B2C doesn't: /avb/urlName
            const secondSegment = urlSegments[1];
            if (validLanguageCodes.includes(secondSegment)) {
                // Language parameter exists, return 4 segments: /de/avb/urlName
                return `${urlSegments.slice(0, 4).join('/')}`;
            }
            // No language parameter, return 3 segments: /avb/urlName
            return `${urlSegments.slice(0, 3).join('/')}`;
        } else {
            // For approval mode, the base URL need to add the approval path
            if (isApprovalMode) {
                return `${urlSegments.slice(0, 5).join('/')}`;
            }
            return `${urlSegments.slice(0, 4).join('/')}`;
        }
    }

    getPathUrl(router: Router): string {
        return router.url.split('?')[0];
    }

    getQueryUrl(router: Router): string {
        const [baseAndQuery] = router.url.split('#');
        return baseAndQuery.split('?')[1];
    }

    getFragmentUrl(router: Router): string {
        return router.url.split('#')[1] ? router.url.split('#')[1] : '';
    }

    getStyleForBreadcrumb(attributeOrder: string | undefined): string {
        if (!attributeOrder) return '';
        return `<span class="mobi-ui-margin-r-xxs">${attributeOrder}</span>`;
    }

    searchContentByEid(eid: string, content: Content): Content | null {
        if (!content) {
            return null;
        }

        if (content?.eId === eid) {
            return content;
        }
        for (const child of content.content) {
            const result = typeof child !== 'string' ? this.searchContentByEid(eid, child) : null;
            if (result) {
                return result;
            }
        }
        return null;
    }

    #buildUrlForChapterHasNoTopic(hierarchy: HierarchyItem[], eId: string, hierarchyLength: number): string {
        return hierarchyLength > 1 ? `${hierarchy[1].eId}#${eId}` : `${eId}`;
    }

    #buildUrlForChapterHasTopic(
        hierarchy: HierarchyItem[],
        eId: string,
        hierarchyLength: number,
        selectedChapter: Content,
    ): string {
        if (hierarchyLength > 2) {
            return `${hierarchy[1].eId}/${URL_PATH.TOPICS}/${hierarchy[2].eId}#${eId}`;
        } else if (hierarchyLength > 1) {
            // Check the element is a topic or not to use / or #
            const element = this.searchContentByEid(eId, selectedChapter);
            if (element?.type === OIV_TYPE.TOPIC) {
                return `${hierarchy[1].eId}/${URL_PATH.TOPICS}/${eId}`;
            }
            return `${hierarchy[1].eId}/${URL_PATH.TOPICS}#${eId}`;
        } else {
            return `${eId}/${URL_PATH.TOPICS}`;
        }
    }

    buildUrlByHierarchy(hierarchy: HierarchyItem[], eId: string, chapterRoot: Content): string {
        const hierarchyLength = hierarchy.length;
        const selectedChapter: Content | null = this.searchContentByEid(
            hierarchyLength <= 1 ? eId : hierarchy[1].eId,
            chapterRoot,
        );
        const numberOfTopics = selectedChapter ? this.countTopicsInChapter(selectedChapter) : 0;
        const chapterUrl =
            numberOfTopics === 0
                ? this.#buildUrlForChapterHasNoTopic(hierarchy, eId, hierarchyLength)
                : this.#buildUrlForChapterHasTopic(
                      hierarchy,
                      eId,
                      hierarchyLength,
                      selectedChapter ? selectedChapter : ({} as Content),
                  );

        return `${URL_PATH.CHAPTERS}/${chapterUrl}`;
    }
    countTopicsInChapter(chapter: Content): number {
        const contents = this.getContentAsArray(chapter);
        return contents.filter(content => content.type === OIV_TYPE.TOPIC).length;
    }

    getUrlPath(content: Content, rootChapter: Content, currentUrl: string, queryUrl: string): string {
        let link = this.buildUrlByHierarchy(content.hierarchy ? content.hierarchy : [], content.eId, rootChapter);
        link = this.enrichQueryParams(link, queryUrl);
        return `${currentUrl}/${link}`;
    }

    enrichQueryParams(link: string, queryUrl: string): string {
        const [nonFragment, fragment] = link.split('#');
        return `${nonFragment}${queryUrl ? '?' + queryUrl : ''}${fragment ? '#' + fragment : ''}`;
    }

    filterInvalidEIdSegments(referenceId: string): string {
        return referenceId
            .split('/')
            .filter(
                segment =>
                    !segment.includes(SUBHEAD_EID) && !segment.includes(SUBTIT_EID) && !segment.includes(TIT_EID),
            )
            .join('/');
    }

    getChapters(mappingContents: Content[]): Content[] {
        // Move the Glossary chapter to the last
        const chapters = this.moveChapterToTheLast(mappingContents, CHAPTER_NAME.GLOSSARY);
        return [...chapters].slice(1);
    }

    moveChapterToTheLast(chapters: Content[], chapterGroupName: string): Content[] {
        const chapter = chapters.find(chapter => chapter.chapterGroupName === chapterGroupName);
        if (chapter) {
            chapters = chapters.filter(chapter => chapter.chapterGroupName !== chapterGroupName);
            chapters.push(chapter);
        }
        return chapters;
    }

    getCoverSheet(mappingContents: Content[]): Content {
        return { ...mappingContents[0] };
    }

    #extractTableSpec(oivModel: OivModel) {
        const headerRowCount = oivModel['oivAttr_header-row-count'];
        const footerRowCount = oivModel['oivAttr_footer-row-count'];
        const colspan = oivModel['oivAttr_colspan'];
        const rowspan = oivModel['oivAttr_rowspan'];
        return {
            ...(oivModel['oivAttr_style'] !== undefined && { attributeStyle: oivModel['oivAttr_style'] }),
            ...(headerRowCount !== undefined && {
                attributeHeaderRowCount: parseInt(headerRowCount) || 0,
            }),
            ...(footerRowCount !== undefined && {
                attributeFooterRowCount: parseInt(footerRowCount) || 0,
            }),
            ...(oivModel['oivAttr_force-complex-table'] !== undefined && {
                attributeForceComplexTable: oivModel['oivAttr_force-complex-table'],
            }),
            ...(oivModel['oivAttr_colspan'] !== undefined && {
                attributeColspan: parseInt(colspan) || 0,
            }),
            ...(oivModel['oivAttr_rowspan'] !== undefined && {
                attributeRowspan: parseInt(rowspan) || 0,
            }),
            ...(oivModel['oivAttr_width'] !== undefined && {
                tableColSpec: { width: oivModel['oivAttr_width'] } as TableColumnSpec,
            }),
        };
    }

    /**
     * If the content is of type `OIV_TYPE.TABLE` and contains a child of type `OIV_TYPE.TABLE_COL_SPEC`,
     * it extracts the column specifications and assigns them to the `tableColSpec` property of the content (OIV_TYPE.TABLE).
     * The `TABLE_COL_SPEC` child is then removed from the content.
     * This needs to be done because the configuration for the table is stored in a special child tag ('colspec') in the XML. But we need it in the parent tag ('table').
     *
     * @param content - The content to process.
     * @returns The processed content with table column specifications assigned.
     */
    #moveColspecForTableToTheParentNode(content: Content): Content {
        if (content.type === OIV_TYPE.TABLE && Array.isArray(content.content)) {
            // get the child which has the type TABLE_COL_SPEC
            let tableColSpec: TableColumnSpec[] = [];
            const tableColSpecContent = content.content.find(
                childContent => childContent.type === OIV_TYPE.TABLE_COL_SPEC,
            );
            if (tableColSpecContent && Array.isArray(tableColSpecContent.content)) {
                tableColSpecContent?.content.forEach(colSpec => {
                    if (colSpec.tableSpec?.tableColSpec) {
                        tableColSpec = tableColSpec.concat(colSpec.tableSpec.tableColSpec);
                    }
                });
                if (content.tableSpec) {
                    content.tableSpec.tableColSpec = tableColSpec;
                } else {
                    content.tableSpec = { tableColSpec };
                }
                // remove the child content which has the type TABLE_COL_SPEC
                content.content = content.content.filter(childContent => childContent.type !== OIV_TYPE.TABLE_COL_SPEC);
            }
        }

        if (Array.isArray(content.content)) {
            content.content.forEach(this.#moveColspecForTableToTheParentNode.bind(this));
        }
        return content;
    }
}
